import axios from "axios";

// ── Base URL resolution ────────────────────────────────────────
// In production (Cloudflare Pages): use /api prefix so the
// Cloudflare Worker proxies requests — browser never hits Render
// directly, so Zscaler never intercepts and strips CORS headers.
//
// In local dev: hit localhost:8000 directly (no Zscaler on loopback).
const BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "production" ? "/api" : "http://localhost:8000");

const api = axios.create({
  baseURL: BASE_URL,
  // ── Timeouts per operation type ──────────────────────────────
  // Default 30s covers standard CRUD operations.
  // AI fill and ESS generation override this per-call (see below).
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// ── Request interceptor — attach auth token + per-route timeout ─
api.interceptors.request.use(
  (config) => {
    // Auth token
    const token = localStorage.getItem("auth_token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // Long-running AI endpoints need extended timeouts.
    // Groq llama-3.3-70b-versatile can take 90-120s for full ESS generation.
    // Groq llama-3.1-8b-instant takes 30-60s for schematic AI fill with
    // multiple components processed sequentially.
    const longRunningRoutes = [
      "/schematic/ai-fill",
      "/ess-gen/generate",
      "/import/upload",
      "/compatibility",
    ];

    const isLongRunning = longRunningRoutes.some((route) =>
      config.url?.includes(route),
    );

    if (isLongRunning) {
      config.timeout = 300000; // 5 minutes
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ── Response interceptor — normalise errors ───────────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const status = error.response.status;
      const detail = error.response.data?.detail || error.message;

      if (status === 401) {
        // Token expired or invalid — clear auth and reload
        localStorage.removeItem("auth_token");
        window.location.href = "/";
      }

      if (status === 502 || status === 503) {
        console.error(
          `[API] Backend unavailable (${status}) — Render may be cold-starting`,
        );
      }

      return Promise.reject(new Error(detail));
    }

    if (error.code === "ECONNABORTED") {
      return Promise.reject(
        new Error(
          "Request timed out — the AI is still processing. " +
            "Please wait and try again.",
        ),
      );
    }

    if (!error.response) {
      // This is the Zscaler symptom — network error with no response
      // means the proxy intercepted and dropped the response.
      console.error(
        "[API] Network error — if on GE corporate network, " +
          "Zscaler proxy may be blocking this request.",
      );
      return Promise.reject(
        new Error(
          "Network error — cannot reach the server. " +
            "If on corporate network, try a mobile hotspot.",
        ),
      );
    }

    return Promise.reject(error);
  },
);

export default api;
